import { imageBucket } from '$lib/supabase';
// import type { ImageStorageBucket } from '$lib/supabase';

// Cache for generated URLs
const urlCache = new Map<string, string>();

// Helper to determine best supported format
function detectBestSupportedFormat(): 'avif' | 'webp' | 'jpg' {
	if (typeof window === 'undefined') return 'jpg';

	const canvas = document.createElement('canvas');
	if (canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0) {
		return 'avif';
	}
	if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
		return 'webp';
	}
	return 'jpg';
}

// Define standard sizes for different devices
const SIZES = {
	mobile: 400, // for screens < 768px
	tablet: 800, // for screens 768px - 1024px
	desktop: 1200 // for screens > 1024px
};

export function logImageError(url: string, error: any) {
	console.error('Image Error:', {
		url,
		error,
		timestamp: new Date().toISOString()
	});
}

export function getImageUrl(path: string, width: number): string {
	try {
		const format = detectBestSupportedFormat();
		const cacheKey = `${path}-${width}-${format}`;

		if (urlCache.has(cacheKey)) {
			return urlCache.get(cacheKey)!;
		}

		const {
			data: { publicUrl: baseUrl }
		} = imageBucket('images').getPublicUrl(path);

		const transformParams = new URLSearchParams({
			width: width.toString(),
			format,
			quality: '80',
			resize: 'cover'
		});

		const finalUrl = `${baseUrl}?${transformParams}`;
		urlCache.set(cacheKey, finalUrl);

		return finalUrl;
	} catch (error) {
		logImageError(path, `URL generation error: ${error}`);
		return '';
	}
}

// Define standard sizes for different devices
export function getImageUrlFeatured(path: string): { src: string; srcset: string } {
	try {
		const sizes = [SIZES.mobile, SIZES.tablet, SIZES.desktop];

		const srcset = sizes
			.map((size) => {
				const url = getImageUrl(path, size);
				return `${url} ${size}w`;
			})
			.join(', ');

		// Use mobile size as default src
		const src = getImageUrl(path, SIZES.mobile);

		return { src, srcset };
	} catch (error) {
		logImageError(path, `Featured image URL generation error: ${error}`);
		return { src: '', srcset: '' };
	}
}

// export function getImageUrlFeatured(path: string, width: number, format: 'avif' | 'webp' | 'jpg') {
// 	if (!path) {
// 		logImageError('empty_path', 'No image path provided');
// 		return '';
// 	}

// 	const bucketName: ImageStorageBucket = 'images';

// 	try {
// 		// Get base URL without transformation
// 		const {
// 			data: { publicUrl: baseUrl }
// 		} = imageBucket(bucketName).getPublicUrl(path);

// 		if (!baseUrl) {
// 			logImageError(path, 'Failed to get public URL');
// 			return '';
// 		}

// 		// Instead of using transform endpoint, use render
// 		const transformUrl = baseUrl.replace(
// 			'/storage/v1/object/public/',
// 			'/storage/v1/render/image/public/'
// 		);

// 		// Simplified parameters
// 		const params = new URLSearchParams({
// 			width: width.toString(),
// 			format,
// 			quality: '80'
// 			// Removed resize parameter
// 		});

// 		const finalUrl = `${transformUrl}?${params.toString()}`;

// 		console.log('Image URL Generation:', {
// 			path,
// 			baseUrl,
// 			transformUrl,
// 			finalUrl,
// 			timestamp: new Date().toISOString()
// 		});

// 		return finalUrl;
// 	} catch (error) {
// 		logImageError(path, `URL generation error: ${error}`);
// 		return '';
// 	}
// }

// Add type declaration for the window property
declare global {
	// eslint-disable-next-line no-unused-vars
	interface Window {
		__loggedUrls?: Set<string>;
	}
}
